import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import type { AssetListQueryOptions } from "@/hooks/clients/assets/useClientAssetsQuerires";
import axiosInstance from "@/lib/axiosInstance";
import { buildFilterByParam } from "@/utils/buildFilterByParam";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import { clientsEndpoints } from "./clients.endpoints";
import type {
  ClientListQueryOptions,
  ClientListResponse,
  TDropdownOption,
  CheckinCommonInstructionsResponse,
} from "./clients.queries.types";
import { coverageEndpoints } from "../service-provider/coverage/coverage.endpoints";
import { AlignmentCoverageEndpoints } from "../alignment/coverage/alignment.coverage.endpoints";
import type { RegionGroupWithCoverage } from "@/types/entities/coverage.entity.ts";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

export const ClientsQueryKeys = {
  list: "clientsList",
  dropdown: "clientsDropdown",
  checkin: "checkin",
  spooferApp: "spooferApp",
  mobileApp: "mobileApp",
  checkInInstruction: "checkInInstruction",
  checkInCommonInstruction: "checkInCommonInstruction",
  coverageList: "client-coverageStateList",
  coveragePreview: "coveragePreviewData",
  coverageCoordinates: "coordinatesCoverage",
} as const;

// Shared static options for client filters
export const CLIENT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "prospect", label: "Prospect" },
] as const;

export const CLIENT_TYPE_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
] as const;

export const getClientList = async (): Promise<any> => {
  const url = "/client/list";
  const response = await axiosInstance.get(url);

  return response.data.data;
};

export const getClientListOverview = async (clientId: string): Promise<any> => {
  const url = `/client/overview/${clientId}`;
  const response = await axiosInstance.get(url);

  return response.data.data;
};

export const getClientListWithFilters = async (
  options: ClientListQueryOptions,
  signal: AbortSignal,
  actingEntityId?: string,
): Promise<ClientListResponse> => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.pageSize.toString());

  if (options.search) {
    params.append("search", options.search);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  // if (options.filters && options.filters.length > 0) {
  //   options.filters.forEach((filter) => {
  //     params.append("filterBy", filter.filterBy);
  //     params.append("filterValue", filter.filterValue.value);
  //   });
  // }

  const filterBy = buildFilterByParam(options.filters);
  if (filterBy) {
    params.append("filterBy", filterBy);
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  const fullUrl = `/client/list?${queryString}`;

  const response = await axiosInstance.get<ClientListResponse>(fullUrl, {
    signal,
    actingEntityId,
  } as CustomAxiosRequestConfig);

  return response.data;
};

export const ClientAssetQueryKeys = {
  list: "clientAssetList",
  dropdown: "clientAssetDropdown",
} as const;

export const getClientsAssetDropdown = async (
  dropDownFieldName: string,
  clientId: string | null,
): Promise<any> => {
  let url = `${clientsEndpoints.clientAssetDropdown}`;
  if (dropDownFieldName === "assetType") {
    url += `client_asset_type`;
  } else if (dropDownFieldName === "manufacturerBrand") {
    url += "client_asset_manufacturer";
  } else if (dropDownFieldName === "locationInstalled") {
    url = `${clientsEndpoints.clientAssetLocationDropdown}=${clientId}&includes=address&field=siteId`;
  }

  const response = await axiosInstance.get(url!);

  return response.data.data;
};

export const getClientAssetsList = async (
  clientId: string | null,
  options?: AssetListQueryOptions,
) => {
  const params: Record<string, string> = {
    page: (options?.page ?? 1).toString(),
    limit: (options?.pageSize ?? 10).toString(),
  };

  if (options?.search) {
    params.search = options.search;
  }

  if (options?.sortBy) {
    params.sortBy = options.sortBy;
  }

  if (options?.sortOrder) {
    params.sortOrder = options.sortOrder;
  }

  const filterBy = buildFilterByParam(options?.filters);
  if (filterBy) {
    params.filterBy = filterBy;
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  const url = `${clientsEndpoints.createAsset}/${clientId}${queryString ? `?${queryString}` : ""}`;
  const response = await axiosInstance.get(url);

  return response.data.data;
};

type TBrand = {
  id: string;
  name: string;
};

type TBrandList = {
  suggested: TBrand[];
  additional: TBrand[];
  totalMatches: number;
};

export const getBrandCatalog = async (signal?: AbortSignal): Promise<TBrand[]> => {
  const { data } = await axiosInstance.get(clientsEndpoints.brandCatalog, { signal });
  return (data?.data?.brands ?? []) as TBrand[];
};

export const getBrandsList = async ({
  searchQuery,
  signal,
}: {
  searchQuery: string;
  signal: AbortSignal;
}): Promise<TBrandList> => {
  const { data } = await axiosInstance.get(`client/search?query=${searchQuery}`, { signal });
  return data?.data;
};

// The backend always returns clientBrandId for client-scoped brands (list, dropdown, create),
// so it is required here — unlike an in-progress selection that isn't persisted yet
// (see SelectedBrand in useBrandManagement.types).
export type TBrands = {
  brands: {
    brandId: string;
    brandName: string;
    clientBrandId: string;
  }[];
};

export const getClientBrandsList = async ({
  clientId,
}: {
  clientId: string | null;
}): Promise<TBrands> => {
  const url = `client/brands/${clientId}`;
  const { data } = await axiosInstance.get(url);

  return data.data;
};

export const getCoverageStateList = async (
  clientId: string | null,
): Promise<RegionGroupWithCoverage[]> => {
  const url = `${clientsEndpoints.getCoverageStateList}/${clientId}`;

  const response = await axiosInstance.get(url);

  return response.data.data.data;
};

export const getCitiesByStateCode = async (
  clientId: string | null,
  stateCode: string,
  resourceType: string,
) => {
  const getCitiesByStateEndpointMap: Record<string, string> = {
    w2: coverageEndpoints.w2GetCitiesByState,
    client: clientsEndpoints.getCitiesByState,
    alignment: AlignmentCoverageEndpoints.coverageCities,
  };

  const baseUrl = getCitiesByStateEndpointMap[resourceType];

  const url = `${baseUrl}/${clientId}/${stateCode}`;

  const response = await axiosInstance.get(url);

  return response.data;
};

export const getPaymentInstructionsDropdown = async (
  dropDownFieldName: string,
  _clientId: string,
): Promise<any> => {
  //   let url = `/clients/${clientId}/payment-instructions/dropdown/${dropDownFieldName}`;
  //   const response = await axiosInstance.get(url);
  //   return response.data.data;

  if (dropDownFieldName === "paymentTerm") {
    const { data } = await axiosInstance.get(
      `${clientsEndpoints.dropdown}?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_payment_terms`,
    );
    return data.data;
  } else if (dropDownFieldName === "accountType") {
    const { data } = await axiosInstance.get(
      `${clientsEndpoints.dropdown}?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=ach_account_type`,
    );
    return data.data;
  } else if (dropDownFieldName === "feeType") {
    const { data } = await axiosInstance.get(
      `${clientsEndpoints.dropdown}?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_fee_type`,
    );
    return data.data;
  }

  return [];
};

export const updateClientCoverage = async (
  clientId: string | null,
  payload: {
    coverage: Array<{
      stateCode: string;
      allCitiesSelected?: boolean;
      selectedCityIds?: number[];
      coverageId: string | null;
      isDeleted?: boolean;
    }>;
  },
) => {
  const url = `/client${clientsEndpoints.updateCoverage}/${clientId}/`;

  const response = await axiosInstance.put(url, payload);

  return response.data;
};

export const getCheckinData = async ({ clientId }: { clientId: string | null }) => {
  const url = `${clientsEndpoints.checkInOut}/${clientId}`;
  const { data } = await axiosInstance.get(url);

  return data.data;
};

export const getCheckinInstructionData = async ({ clientId }: { clientId: string | null }) => {
  const url = `${clientsEndpoints.checkInOut}/instruction-types/${clientId}`;
  const { data } = await axiosInstance.get(url);

  return data.data;
};

export const getCheckinCommonInstructionData = async ({
  clientId,
}: {
  clientId: string | null;
}): Promise<CheckinCommonInstructionsResponse> => {
  const url = `${clientsEndpoints.checkInOut}/common-instructions/${clientId}`;
  const { data } = await axiosInstance.get(url);

  return data.data;
};

type TAddCustomBrandResponse = {
  brands: {
    clientBrandId: string;
    brandId: string;
    brandName: string;
  }[];
};

export type TAddBrandPayload = {
  brands: {
    brandId: string | null;
    clientBrandId: string | null;
    customBrandName?: string;
    externalId?: string | null;
    isDeleted: boolean;
  }[];
};

export const addCustomBrand = async ({
  payload,
  clientId,
}: {
  payload: TAddBrandPayload;
  clientId: string | null;
}): Promise<TAddCustomBrandResponse> => {
  const { data } = await axiosInstance.put(`client/brands/${clientId}`, payload);
  return data.data;
};

// SERV-2532: typing this as Promise<PaymentInvoicingDetails> surfaces a real nullability mismatch
// in ClientOnboarding/PaymentInstructions.tsx (a form reset that assumes non-null address strings).
// Left untyped here so that fix stays its own change; callers that need the shape assert it.
export const getPaymentInvoicingDetails = async (_clientId: string) => {
  const { data } = await axiosInstance.get(`${clientsEndpoints.paymentInvoicing}/${_clientId}`);
  return data.data;
};

export type TEquipment = {
  id: number;
  name: string;
  pricePerDay: boolean;
  disposalFee: boolean;
  dropOffPickUpFee: boolean;
};

export const getEquipmentList = async (): Promise<TEquipment[]> => {
  const url = `/dropdown?type=Equipment&field=name&field=pricePerDay&field=disposalFee&field=dropOffPickUpFee&limit=50`;
  const { data } = await axiosInstance.get(url);

  return data.data;
};

export type TAddedEquipment = {
  id: string;
  equipmentId: string;
  equipmentName: string;
  pricePerDay: string | null;
  disposalFee: string | null;
  dropOffPickUpFee: string | null;
  isDeleted?: boolean;
};

export const getAddedEquipments = async ({
  clientId,
}: {
  clientId: string | null;
}): Promise<TAddedEquipment[]> => {
  const url = `/client/equipment-rates/${clientId}?page=1&limit=10`;
  const { data } = await axiosInstance.get(url);

  return data.data.equipmentRates;
};

export const getClientDropDown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
  searchParams?: TSearchParams,
): Promise<any> => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  if (key == "businessName") {
    params.append("type", "Clients");
    params.append("field", "id");
    params.append("field", key);
  }

  if (key == "status") {
    params.append("type", "LookupTypes");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_status");
  }

  //type=Clients&field=id&includes=clientSuccessManager

  if (key == "clientSuccessManager") {
    params.append("type", "Clients");
    params.append("field", "id");
    params.append("field", key);
  }

  //?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_type
  if (key === "clientType") {
    params.append("type", "LookupTypes");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_type");
  }

  //?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_payment_terms
  if (key === "paymentTerms") {
    params.append("type", "LookupTypes");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_payment_terms");
  }

  // Client Assets tab filters (same as location assets dropdown contract)
  if (key === "assetType") {
    params.append("type", "LookupTypes");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_asset_type");
  }
  if (key === "assetId") {
    params.append("type", "ClientAssets");
    params.append("field", "id");
    params.append("field", "assetId");
    params.append("alias", JSON.stringify({ source: "assetId", target: "label" }));
    if (searchParams?.filterValue) {
      params.append("filterBy", "clientId");
      params.append("filterValue", searchParams.filterValue);
    }
  }
  if (key === "manufacturerBrand") {
    params.append("type", "LookupTypes");
    params.append("field", "label");
    params.append("field", "value");
    params.append("filterBy", "type");
    params.append("filterValue", "client_asset_manufacturer");
  }
  if (key === "modelNumber") {
    params.append("type", "ClientAssets");
    params.append("field", "id");
    params.append("field", "modelNumber");
    params.append("alias", JSON.stringify({ source: "modelNumber", target: "label" }));
    if (searchParams?.filterValue) {
      params.append("filterBy", "clientId");
      params.append("filterValue", searchParams.filterValue);
    }
  }
  if (key === "serialNumber") {
    params.append("type", "ClientAssets");
    params.append("field", "id");
    params.append("field", "serialNumber");
    params.append("alias", JSON.stringify({ source: "serialNumber", target: "label" }));
    if (searchParams?.filterValue) {
      params.append("filterBy", "clientId");
      params.append("filterValue", searchParams.filterValue);
    }
  }
  if (key === "locationsManaged") {
    params.append("type", "ClientLocations");
    params.append("field", "siteId");
    if (searchParams?.filterValue) {
      params.append("filterBy", "id");
      params.append("filterValue", searchParams.filterValue);
    }
  }

  const assetFilterKeys = [
    "assetType",
    "assetId",
    "manufacturerBrand",
    "modelNumber",
    "serialNumber",
  ];
  if (key && assetFilterKeys.includes(key)) {
    const limit = searchParams?.limit ?? 200;
    params.append("limit", String(limit));
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);

  const response = await axiosInstance.get<any>(
    `${clientsEndpoints.clientDropdown}?${queryString}`,
    {
      signal,
    },
  );

  return response.data;
};
export interface FuzzyMatchPayload {
  polygonGeoJson: {
    type: "Polygon";
    coordinates: number[][][];
  };
  strictAdherence: boolean;
  locationNames?: Array<{
    name: string;
    isState: boolean;
    allCitiesSelected: boolean;
  }>;
  dispatchAddress?: {
    id: string | null;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
    latitude: string;
    longitude: string;
  };
}

export interface IFuzzyPolygonCoordinates {
  success: boolean;
  data: IClientPolygonData;
}

export interface IClientPolygonData {
  clientId: string;
  polygonGeoJson: IPolygonGeoJson;
  strictAdherence: boolean;
  createdBy: string;
  updatedBy: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

export interface IPolygonGeoJson {
  type: "Polygon";
  coordinates:
    | number[][][] // LinearRing (array of [lng, lat])
    | null;
}

export interface FuzzyMatchResponse {
  success: boolean;
  data: {
    message: string;
    clientId: string;
    coverageAreasUpdated: boolean;
    locationMatches: Array<{
      input: string;
      matched: boolean;
      isState: boolean;
      allCitiesSelected: boolean;
      cityId: number;
      city: string;
      state: string;
      stateCode: string;
      similarity: number;
    }>;
    coverageAreasSummary: {
      created: number;
      updated: number;
      statesAffected: string[];
    };
  };
}

export const fuzzyMatchCities = async (
  clientId: string | null,
  payload: FuzzyMatchPayload,
  resourceType: string,
): Promise<FuzzyMatchResponse> => {
  let baseUrl = clientsEndpoints.fuzzyMatchCities;
  if (resourceType == "w2") {
    baseUrl = `${coverageEndpoints.fuzzyMatchCities}`;
  }
  const url = `${baseUrl}/${clientId}`;
  const response = await axiosInstance.put<FuzzyMatchResponse>(url, payload);
  return response.data;
};

export const fuzzyMatchPolygonCoordinates = async (
  clientId: string | null,
  resourceType: string,
): Promise<IFuzzyPolygonCoordinates> => {
  let baseUrl = `${clientsEndpoints.fuzzyMatchCities}/${clientId}`;
  if (resourceType == "w2") {
    baseUrl = `${coverageEndpoints.fuzzyMatchCities}/${clientId}`;
  }
  const response = await axiosInstance.get<IFuzzyPolygonCoordinates>(baseUrl);
  return response.data;
};

export const coveragePreviewData = async (
  sourceId: string,
  coordinates: number[][][],
  resourceType: string,
) => {
  let baseEndpoint;
  if (resourceType === "w2") {
    baseEndpoint = `service-providers/w2-coverage`;
  } else {
    baseEndpoint = `client/coverage`;
  }

  const url = `${baseEndpoint}/${clientsEndpoints.previewCoverage(sourceId, coordinates)}`;
  const response = await axiosInstance.get<any>(url);
  return response.data.data;
};

export const deleteCoordinatesCoverage = async (
  clientId: string,
  coordinates: number[][][],
  resourceType: string,
) => {
  const baseEndpoint = resourceType === "w2" ? `service-providers/w2-coverage` : `client/coverage`;
  const url = `${baseEndpoint}/${clientsEndpoints.coordinatesCoverage(clientId, coordinates)}`;
  const response = await axiosInstance.delete<any>(url);
  return response.data.data;
};

export const getClientOverview = async (clientId: string) => {
  const { data } = await axiosInstance.get(`${clientsEndpoints.getClientOverview}/${clientId}`);
  return data.data;
};

export const getMobileAppOptions = async (): Promise<TDropdownOption[]> => {
  const url =
    "/dropdown?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_checkinout_mobile_app";
  const { data } = await axiosInstance.get(url);

  return data.data;
};

export const getSpooferAppOptions = async (): Promise<TDropdownOption[]> => {
  const url =
    "/dropdown?type=LookupTypes&field=label&field=value&filterBy=type&filterValue=client_checkinout_spoofer_app";
  const { data } = await axiosInstance.get(url);

  return data.data;
};

export const getCoverageStateListWithSearch = async (clientId: string | null, search?: string) => {
  const params = new URLSearchParams();
  if (search) {
    params.append("search", search);
  }
  const queryString = params.toString();
  const url = `${clientsEndpoints.clientCoverageList}/${clientId}${queryString ? `?${queryString}` : ""}`;
  const response = await axiosInstance.get(url);
  return response.data.data;
};
